import React from "react";

import { useFormikContext } from "formik";
import PropTypes from "prop-types";

import { DealSetupAttachmentsModal } from "manager/components/Modal";
import { ATTACHMENT_TYPES } from "manager/config/constants";
import { SwitchWithCheckboxList } from "shared/components/Form";
import Settings from "shared/components/Settings";
import Skeleton from "shared/components/Skeleton";
import { APPLICANT_TYPES, CONTENT_TYPE } from "shared/config/constants";
import useModal from "shared/hooks/useModal";

import ApplicationSetupHeader from "../Header";
import { getAttachmentsFilter, mapLegacyAttachmentItems } from "../utils";

const applicantAdditionalAttachmentsForIncome =
  "requiresApplicantAdditionalAttachementsForIncome";
const guarantorAdditionalAttachmentsForIncome =
  "requiresGuarantorAdditionalAttachementsForIncome";
const occupantAdditionalAttachmentsForIncome =
  "requiresOccupantAdditionalAttachementsForIncome";

const getRequiredAttachments = getAttachmentsFilter(
  ATTACHMENT_TYPES.UNVERIFIED_INCOME
);

const mapItems = (items) =>
  mapLegacyAttachmentItems(items, {
    showOcrTooltip: true,
  });

const RequiredAttachmentsUnverifiedIncomeLegacy = ({
  loading,
  disabled,
  forSettingsLabel = "Settings",
}) => {
  const { values, setFieldValue } = useFormikContext();
  const { openModalDialog } = useModal();

  const openAttachmentsModal = ({ items, dealSetupId: objectId, title, newItemProps }) =>
    openModalDialog(DealSetupAttachmentsModal, {
      title,
      items,
      objectId,
      contentType: CONTENT_TYPE.dealSetup,
      forSettingsLabel,
      newItemProps,
    });

  const onChangeAttachment = (id, checked) => {
    const index = values.requiredAttachments?.findIndex(
      (item) => item.id === id
    );
    setFieldValue(`requiredAttachments[${index}].isActive`, checked);
  };

  return (
    <Settings
      header={
        <Skeleton loading={loading}>
          <ApplicationSetupHeader
            title="Unverified Income Setup"
            subTitle="Please manage the attachments that you want to require from applicants, occupants and guarantors in case their income & balances are not verified"
          />
        </Skeleton>
      }
      nodes={[
        {
          key: 1,
          component: (
            <Skeleton loading={loading}>
              <SwitchWithCheckboxList
                title="Applicant Attachments"
                items={mapItems(
                  getRequiredAttachments(values, APPLICANT_TYPES.applicant)
                )}
                onManageList={() => {
                  openAttachmentsModal({
                    title: "Manage Applicant Attachments",
                    dealSetupId: values.id,
                    items: mapItems(
                      getRequiredAttachments(values, APPLICANT_TYPES.applicant)
                    ),
                    newItemProps: {
                      attachmentType: ATTACHMENT_TYPES.UNVERIFIED_INCOME,
                      applicantType: APPLICANT_TYPES.applicant,
                    },
                  });
                }}
                onChange={onChangeAttachment}
                value={values[applicantAdditionalAttachmentsForIncome]}
                onChangeTitle={(value) => {
                  setFieldValue(applicantAdditionalAttachmentsForIncome, value);
                }}
                disabled={disabled}
              />
            </Skeleton>
          ),
        },
        {
          key: 2,
          component: (
            <Skeleton loading={loading}>
              <SwitchWithCheckboxList
                title="Occupant Attachments"
                description="over 18 years old"
                items={mapItems(
                  getRequiredAttachments(values, APPLICANT_TYPES.occupant)
                )}
                onManageList={() => {
                  openAttachmentsModal({
                    title: "Manage Occupant Attachments",
                    dealSetupId: values.id,
                    items: mapItems(
                      getRequiredAttachments(values, APPLICANT_TYPES.occupant)
                    ),
                    newItemProps: {
                      attachmentType: ATTACHMENT_TYPES.UNVERIFIED_INCOME,
                      applicantType: APPLICANT_TYPES.occupant,
                    },
                  });
                }}
                onChange={onChangeAttachment}
                value={values[occupantAdditionalAttachmentsForIncome]}
                onChangeTitle={(value) => {
                  setFieldValue(occupantAdditionalAttachmentsForIncome, value);
                }}
                disabled={disabled}
              />
            </Skeleton>
          ),
        },
        {
          key: 3,
          component: (
            <Skeleton loading={loading}>
              <SwitchWithCheckboxList
                title="Guarantor Attachments"
                items={mapItems(
                  getRequiredAttachments(values, APPLICANT_TYPES.guarantor)
                )}
                onManageList={() => {
                  openAttachmentsModal({
                    title: "Manage Guarantor Attachments",
                    dealSetupId: values.id,
                    items: mapItems(
                      getRequiredAttachments(values, APPLICANT_TYPES.guarantor)
                    ),
                    newItemProps: {
                      attachmentType: ATTACHMENT_TYPES.UNVERIFIED_INCOME,
                      applicantType: APPLICANT_TYPES.guarantor,
                    },
                  });
                }}
                onChange={onChangeAttachment}
                value={values[guarantorAdditionalAttachmentsForIncome]}
                onChangeTitle={(value) => {
                  setFieldValue(guarantorAdditionalAttachmentsForIncome, value);
                }}
                disabled={disabled}
              />
            </Skeleton>
          ),
        },
      ]}
    />
  );
};

RequiredAttachmentsUnverifiedIncomeLegacy.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  forSettingsLabel: PropTypes.string,
};

RequiredAttachmentsUnverifiedIncomeLegacy.defaultProps = {
  loading: false,
  disabled: false,
  forSettingsLabel: "Settings",
};

export default RequiredAttachmentsUnverifiedIncomeLegacy;
